import config from "@app/config";
import { addToBackInStock, addToCart, formatPrice } from "@app/scripts/utils";
import type { Product, RecentlyViewed, Variant, VariantChangeArgs } from "@app/scripts/types";
import { getLocalisation } from "../utils/getLocalisation";


class ProductDetails extends HTMLElement {
  product: Product;
  lowestProduct: Product;
  highestProduct: Product;
  productForm: HTMLFormElement | null;
  productBISForm: HTMLFormElement | null;
  addToCartWrapper: HTMLButtonElement | null;
  sizeText: HTMLElement | null;
  errorMessage: HTMLElement | null;
  stockLevels: { title: string; level: "regular_stock" | "low_stock" | "out_of_stock"; quantity: number }[];
  isGiftCard: Boolean;
  selectSizeGuideBtns: NodeListOf<HTMLElement>;
  giftCardProducts?: Product[];
  qtyInput?: HTMLInputElement | null;
  isProduct: boolean = false;
  priceSpan: HTMLElement | null;
  priceRange: HTMLElement | null;
  compareSpan: HTMLElement | null;
  isComingSoon: any;

  constructor() {
    super();

    this.product = JSON.parse((this.querySelector("[data-json]") as HTMLElement)?.innerText);
    this.lowestProduct = JSON.parse((this.querySelector("[data-lowest-product]") as HTMLElement)?.innerText);
    this.highestProduct = JSON.parse((this.querySelector("[data-highest-product]") as HTMLElement)?.innerText);
    this.stockLevels = JSON.parse((this.querySelector("[data-productstock]") as HTMLElement)?.innerText)?.map(
      (x: string) => ({
        title: x.split(":")[0],
        level: x.split(":")[1],
        quantity: x.split(":")[2],
      })
    );

    this.isComingSoon = this.product.tags?.find((tag) => tag.toLocaleLowerCase().includes("coming"));
    this.compareSpan = this.querySelector("#compare-at-price");
    this.priceSpan = this.querySelector("#money-price");
    this.priceRange = this.querySelector('#money-range');
    this.productForm = this.querySelector(".product__form");
    this.productBISForm = this.querySelector(".product__bis-form");
    this.addToCartWrapper = this.querySelector(".product__add-to-cart");
    this.sizeText = this.querySelector("[data-sizetext]");
    this.qtyInput = this.querySelector("[data-qtyinput]");
    this.errorMessage = this.querySelector("[data-error-message]");
    this.isGiftCard = JSON.parse(
      (this.querySelector("[data-productisgiftcard]") as HTMLElement)?.innerText || "{}"
    )?.isGiftCard;
    this.selectSizeGuideBtns = this.querySelectorAll("[data-opensizeguide]");
    // this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleBISSubmit = this.handleBISSubmit.bind(this);
    this.handleScrollToSizeGuide = this.handleScrollToSizeGuide.bind(this);
    // this.disableAtcOnNotSelected = this.disableAtcOnNotSelected.bind(this);
    this.handleFirstProduct = this.handleFirstProduct.bind(this);
    this.handlePriceDisplay = this.handlePriceDisplay.bind(this);
    // this.setPricing = this.setPricing.bind(this);
  }

  connectedCallback() {
    const is_quick_add = this.getAttribute("quick-add") === "true";
    if (!is_quick_add) {
      this.addToRecentlyViewed();
    }

    this.handleFirstProduct();

    this.handlePriceDisplay();

    this.productForm?.addEventListener("submit", this.handleAddToCart);

    this.productBISForm?.addEventListener("submit", this.handleBISSubmit);

    this.selectSizeGuideBtns?.forEach((btn) => btn.addEventListener("click", this.handleScrollToSizeGuide));

    // this.productForm
    //   ?.querySelectorAll(".product__variant-buttons input[type=radio]")
    //   ?.forEach((input) => input.addEventListener("change", this.handleVariantChange));

    const variantChangeProperties = {
      productBISForm: this.productBISForm,
      product: this.product,
      stockLevels: this.stockLevels,
      isGiftCard: this.isGiftCard,
      sizeText: this.sizeText,
      isProduct: this.isProduct,
      setPricing: setPricing,
      errorMessage: this.errorMessage,
      isComingSoon: this.isComingSoon,
      addToCartWrapper: this.addToCartWrapper,
    };

    this.productForm?.querySelectorAll(".product__variant-buttons input[type=radio]")?.forEach((input) =>
      input.addEventListener("change", (event) => {
        handleVariantChange({
          e: event,
          ...variantChangeProperties,
        });
      })
    );

    // Check if this is a single variant product. If so, force a variant change selecting the first variant.
    if (this.product?.variants?.length === 1 && !this.product.tags.includes("category--gift-card")) {
      variantChange({
        ...variantChangeProperties,
      });
    } else {
      disableAtcOnNotSelected();
      const checked = this.productForm?.querySelector(".product__variant-buttons input[type=radio]:checked");
      if (checked) {
        variantChange({
          checked: checked,
          ...variantChangeProperties,
        });
      }
    }

    if (this.isComingSoon) {
      (this.querySelector(".product__qty") as HTMLInputElement).classList.add("hidden");
      this.addToCartWrapper?.classList.add("hidden");

      (this.querySelector(".product_oos") as HTMLInputElement).classList.remove("hidden");

      if (window.location.href.includes("email=")) {
        (this.querySelector(".product_oos-message-content .sold_out") as HTMLElement).classList.add("hidden");
        (this.querySelector(".product_oos-message-content .success") as HTMLElement).classList.remove("hidden");
        (this.querySelector(".product_oos-message-content .sold_out-p") as HTMLElement).classList.add("hidden");
        (this.querySelector(".product_oos-message-content .success-p") as HTMLElement).classList.remove("hidden");
      } else {
        (this.querySelector(".product_oos-message-content .sold_out") as HTMLElement).classList.remove("hidden");
        (this.querySelector(".product_oos-message-content .success") as HTMLElement).classList.add("hidden");
        (this.querySelector(".product_oos-message-content .sold_out-p") as HTMLElement).classList.remove("hidden");
        (this.querySelector(".product_oos-message-content .success-p") as HTMLElement).classList.add("hidden");
      }
    }
  }

  handleFirstProduct() {
    const localisation = getLocalisation();

    const priceContainer = this.querySelector(".price__container") as HTMLElement;
    if (!priceContainer) {
      return;
    }

    this.isProduct = priceContainer.dataset.isproduct === "true";

    if (!this.lowestProduct || !this.highestProduct) {
      return;
    }

    const lowest = formatPrice(this.lowestProduct.price_min / 100, localisation?.currency_code || "AUD");
    const highest = formatPrice(this.highestProduct.price_max / 100, localisation?.currency_code || "AUD");
    const compareAtLowest = formatPrice(this.lowestProduct.compare_at_price_min / 100, localisation?.currency_code || "AUD");
    const compareAtHighest = formatPrice(this.highestProduct.compare_at_price_max / 100, localisation?.currency_code || "AUD");

    if (this.priceSpan) {
      this.priceSpan.classList.add('hide');
    }

    if (this.priceRange) {
      this.priceRange.innerHTML = lowest === highest
        ? `${lowest}`
        : (compareAtLowest && compareAtHighest && (compareAtLowest !== lowest || compareAtHighest !== highest))
          ? `<s>${compareAtLowest} - ${compareAtHighest}</s> &nbsp ${lowest} - ${highest}`
          : `${lowest} - ${highest}`;

    }

    // "this" based variables used above provide inconsistent data for the "compareSpan" variable below
    // Checking if a size is selected creates a consistent outcome to show the correct pricing structures on the product page
    const productSizeRadioButtons = document.querySelectorAll('.product__variant-buttons input[type="radio"][name="Size"]');
    let sizeSelected = false;
    productSizeRadioButtons?.forEach(function (size) {
      if ((size as HTMLInputElement)?.checked) {
        sizeSelected = true;
      }
    });

    // if the text content of this.priceRange contains a "-" then it is a price range
    const isPriceRange = this.priceRange?.textContent?.includes("-");

    if (this.compareSpan && !sizeSelected && this.priceRange && isPriceRange) {
      this.compareSpan.innerHTML = "";
    }
  }

  handlePriceDisplay() {
    const vaiantOption = this.querySelectorAll("input[name='Size']");

    const togglePriceDisplay = () => {
      this.priceSpan?.classList.remove('hide');
      this.priceRange?.classList.add('hide');
    }

    if (vaiantOption.length) {
      vaiantOption.forEach((input) => {
        if (input.hasAttribute('checked')) {
          togglePriceDisplay();
        }
        input.addEventListener("click", () => togglePriceDisplay());
      })
    }
  }

  // disableAtcOnNotSelected() {
  //   const atcButton = this.querySelector(".add-to-cart") as HTMLButtonElement;
  //   const id = this.querySelector("input[name=id]") as HTMLInputElement;
  //   atcButton.disabled = !id.value.length ? true : false;
  //   atcButton.ariaDisabled = !id.value.length ? "true" : "false";
  // }

  async handleBISSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (this.productBISForm === null) return;
    const data = new FormData(this.productBISForm);
    const email = data.get("email") as string;
    const variantId = parseInt(data?.get("variant") as string);
    const productId = this.product?.id;
    const shopDomain = data.get("store") as string;
    const shop = shopDomain.split(".");
    const store = shop.length ? shop[0] : "";
    const addMe = data.get("addMe") as unknown as boolean;

    if (store) {
      const res = await addToBackInStock({ email, variantId, productId, store, addMe });
    }
  }

  async handleAddToCart(e: SubmitEvent) {
    e.preventDefault();
    if (this.productForm === null) return;
    const data = new FormData(this.productForm);
    const selectedId = data.get("id") as string;

    this.errorMessage?.classList.add("hidden");

    if (!selectedId) return;

    const res = await addToCart({
      id: parseInt(selectedId),
      qty: this.qtyInput?.value || 1,
    });

    if (res.status === 200) {
      return false;
    } else {
      if (this.errorMessage) {
        this.errorMessage.innerHTML = res?.description ?? "There was an error adding to the cart.";
        this.errorMessage.classList.remove("hidden");
      }
    }
  }

  handleScrollToSizeGuide(e: Event) {
    e?.preventDefault();
    const sizeGuideBtn = this.querySelector("[data-slidetoggle='#box-size_guide']") as HTMLElement;
    sizeGuideBtn?.click();
    sizeGuideBtn?.scrollIntoView({
      behavior: "smooth",
    });
  }

  // handleVariantChange(e: Event) {
  //   const target = e.target as HTMLInputElement;
  //   if (target !== null) {
  //     this.variantChange(target);
  //   }
  //   // this.isGiftCard ? this.giftVariantChange(e) : this.variantChange(e.target);
  // }

  // setPricing(price: number, compare: number | null) {
  //   if (this.priceSpan) {
  //     this.priceSpan.innerHTML = formatPrice(price / 100);
  //   }

  //   if (this.compareSpan && compare) {
  //     this.compareSpan.innerHTML = formatPrice(compare / 100);
  //   }
  // }

  /**
   * Handle the variant change event. An empty target means the first variant will be selected.
   */
  // variantChange(target?: HTMLInputElement) {
  //   const hiddenId = this.querySelector("input[name=id]") as HTMLInputElement;
  //   const hiddenBisId = this.productBISForm?.querySelector("input[name=variant]") as HTMLInputElement;

  //   const oosDetails = this.querySelector(".product_oos") as HTMLInputElement;
  //   const productQty = this.querySelector(".product__qty") as HTMLInputElement;
  //   const selectedVariant = !target
  //     ? this.product?.variants?.[0]
  //     : this.product?.variants?.find((variant) => variant.option2 === target?.value);
  //   const isOos = target?.closest(".button")?.classList.contains("oos");
  //   const selectedStockLevel = this.stockLevels.find((lvl) => lvl.title === selectedVariant?.title)?.level ?? null;

  //   if (target && !this.isGiftCard) {
  //     if (this.sizeText !== null) {
  //       this.sizeText.innerHTML = `<strong>Size:</strong> <span>${target.value}</span>`;
  //     }
  //   }

  //   if (!selectedVariant) return;

  //   if (this.isProduct) {
  //     this.setPricing(selectedVariant.price, selectedVariant.compare_at_price);
  //     this.isProduct = false;
  //   }

  //   const selectedId = selectedVariant.id.toString();

  //   hiddenId.value = selectedId;
  //   if (!this.isGiftCard) {
  //     hiddenBisId.value = selectedId;
  //   }

  // this.disableAtcOnNotSelected();

  //   this.errorMessage?.classList.add("hidden");
  //   this.querySelector(".product__low-stock")?.classList.add("hidden");
  //   this.querySelector(".product__out-of-stock")?.classList.add("hidden");

  //   if (selectedStockLevel === "low_stock") {
  //     this.querySelector(".product__low-stock")?.classList.remove("hidden");
  //   }
  //   if (selectedStockLevel === "out_of_stock") {
  //     this.querySelector(".product__out-of-stock")?.classList.remove("hidden");
  //   }

  //   if (isOos || this.isComingSoon) {
  //     oosDetails?.classList.remove("hidden");
  //     productQty?.classList.add("hidden");
  //     this.addToCartWrapper?.classList.add("hidden");
  //     if (window.location.href.includes("email=")) {
  //       (this.querySelector(".product_oos-message-content .sold_out") as HTMLElement).classList.add("hidden");
  //       (this.querySelector(".product_oos-message-content .success") as HTMLElement).classList.remove("hidden");
  //       (this.querySelector(".product_oos-message-content .sold_out-p") as HTMLElement).classList.add("hidden");
  //       (this.querySelector(".product_oos-message-content .success-p") as HTMLElement).classList.remove("hidden");
  //     } else {
  //       (this.querySelector(".product_oos-message-content .sold_out") as HTMLElement).classList.remove("hidden");
  //       (this.querySelector(".product_oos-message-content .success") as HTMLElement).classList.add("hidden");
  //       (this.querySelector(".product_oos-message-content .sold_out-p") as HTMLElement).classList.remove("hidden");
  //       (this.querySelector(".product_oos-message-content .success-p") as HTMLElement).classList.add("hidden");
  //     }
  //     (this.querySelector(".product__bis-form-email input") as HTMLInputElement).focus();
  //   } else {
  //     oosDetails?.classList.add("hidden");
  //     productQty?.classList.remove("hidden");
  //     this.addToCartWrapper?.classList.remove("hidden");
  //   }

  //   this.handleVariantParamChange(selectedId);

  //   window.dispatchEvent(new CustomEvent("variantchange", { detail: selectedVariant.id }));
  // }

  // handleVariantParamChange(selectedId: string) {
  //   const url = new URL(window.location.href);
  //   const params = new URLSearchParams(url.search);
  //   const variant = params.get("variant");
  //   if (variant === selectedId) return;
  //   params.set("variant", selectedId);
  //   const newurl =
  //     window.location.protocol + "//" + window.location.host + window.location.pathname + "?" + params.toString();
  //   window.history.replaceState({ path: newurl }, "", newurl);
  // }

  giftVariantChange() {
    const hiddenId = this.querySelector("input[name=id]") as HTMLInputElement;
    const type = this.querySelector("[name='giftcard_type']:checked") as HTMLInputElement;
    const typeValue: string = type
      ? type?.value
      : this.product?.title?.toLowerCase()?.includes("digital") ||
        this.product?.title?.toLowerCase()?.includes("e-gift")
        ? "digital"
        : "physical";

    const productVariantBtns = this.productForm?.querySelector(".product__variant-buttons") as HTMLInputElement;
    const variantId = productVariantBtns?.querySelector("[name='variant_id']:checked") as HTMLInputElement;
    let lastSelectedPrice: number | undefined;
    if (variantId?.dataset.price) {
      lastSelectedPrice = parseInt(variantId.dataset.price);
    }

    const buildChips = (variants?: Variant[]) => {
      return (
        variants
          ?.map(
            (variant) => /* HTML*/ `<label class="button button--chip">
              <input type="radio" value="${variant.id}" name="variant_id" data-price="${variant.price}" ${lastSelectedPrice === variant.price ? "checked" : ""
              }>
              <span>$${variant.price / 100}</span>
            </label>`
          )
          .join("") ?? ""
      );
    };

    if (!productVariantBtns || !this.productForm) return;

    if (this.giftCardProducts) {
      if (typeValue === "digital") {
        const str_to_parse = JSON.stringify(this.giftCardProducts).replace(
          '<meta name="robots" content="noindex,follow"/>',
          ""
        );
        const gf_json = JSON.parse(str_to_parse);
        const variants = gf_json?.find(
          (product: { title: string }) =>
            product?.title?.toLowerCase()?.includes("digital") || product?.title?.toLowerCase()?.includes("e-gift card")
        )?.variants;
        productVariantBtns.innerHTML = buildChips(variants);
      } else if (typeValue === "physical") {
        const str_to_parse = JSON.stringify(this.giftCardProducts).replace(
          '<meta name="robots" content="noindex,follow"/>',
          ""
        );
        const gf_json = JSON.parse(str_to_parse);
        const variants = gf_json
          ?.filter(
            (product: { title: string }) =>
              !product?.title?.toLowerCase()?.includes("digital") ||
              !product?.title?.toLowerCase()?.includes("e-gift card")
          )
          ?.sort((a: { price: number }, b: { price: number }) => a.price - b.price)
          ?.map((product: { variants: any[] }) => product.variants?.[0]);
        productVariantBtns.innerHTML = buildChips(variants);
      }
    }

    disableAtcOnNotSelected();

    // re-attach listeners
    this.productForm?.querySelectorAll(".product__variant-buttons input[type=radio]")?.forEach((input) => {
      input.removeEventListener("change", (event) => {
        handleVariantChange({ e: event });
      });
      input.addEventListener("change", (event) => {
        handleVariantChange({ e: event });
      });
    });

    // check the first option if nothing matches
    if (!productVariantBtns?.querySelector("[name='variant_id']:checked")) {
      productVariantBtns.querySelector("[name='variant_id']")?.setAttribute("checked", "true");
    }

    this.querySelector(`[value='${typeValue}']`)?.setAttribute("checked", "true");

    if (!hiddenId) {
      return;
    }

    const finalValue = (productVariantBtns?.querySelector("[name='variant_id']:checked") as HTMLInputElement)?.value;

    if (finalValue) hiddenId.value = finalValue;
  }

  /**
   * Add the product handle to the recently viewed localstorage obj.
   */
  addToRecentlyViewed() {
    let recently_viewed: RecentlyViewed[] = JSON.parse(localStorage.getItem(config.keys.recently_viewed) ?? "[]");
    const index = recently_viewed.findIndex(({ id }) => id === this.product.id);
    const entry = {
      id: this.product.id,
      time: parseInt(Date.now().toString()),
    };
    index !== -1 ? (recently_viewed[index] = entry) : recently_viewed.push(entry);

    recently_viewed.sort((a, b) => b.time - a.time);

    if (recently_viewed.length >= 10) {
      recently_viewed = recently_viewed.slice(0, 10);
    }

    localStorage.setItem(config.keys.recently_viewed, JSON.stringify(recently_viewed));
  }
}

customElements.define("product-details", ProductDetails);

export function setPricing(price: number, compare: number) {
  const localisation = getLocalisation();

  const priceSpan = document.querySelector("#money-price");
  const compareSpan = document.querySelector("#compare-at-price");

  if (priceSpan) {
    priceSpan.innerHTML = formatPrice(price / 100, localisation?.currency_code || "AUD");
  }

  if (compareSpan && compare) {
    compareSpan.innerHTML = formatPrice(compare / 100, localisation?.currency_code || "AUD");
  }
}

export function variantChange({
  target,
  productBISForm,
  product,
  stockLevels,
  isGiftCard,
  sizeText,
  isProduct,
  setPricing,
  errorMessage,
  isComingSoon,
  addToCartWrapper,
}: VariantChangeArgs) {
  const hiddenId = document.querySelector("input[name=id]") as HTMLInputElement;
  const hiddenBisId = productBISForm?.querySelector("input[name=variant]") as HTMLInputElement;
  const oosDetails = document.querySelector(".product_oos") as HTMLInputElement;
  const productQty = document.querySelector(".product__qty") as HTMLInputElement;
  let selectedVariant: any;
  const optionTypes = product?.options;
  const optionType = target?.name;

  if (!target) {
    const idInput = document.querySelector(".product__form input[name=id]") as HTMLInputElement;
    selectedVariant = idInput?.value || product?.variants?.[0];
  } else {
    // Find the index of the selected option name (e.g., "Colour", "Size")
    const optionIndex = optionTypes?.findIndex((opt: string) => opt === optionType);
    // optionN property is 1-based (option1, option2, ...)
    const optionProp = (typeof optionIndex === 'number' && optionIndex !== -1) ? `option${optionIndex + 1}` : undefined;
    selectedVariant = product?.variants?.find((variant: any) => {
      // If data-variant-id is present, use it directly
      if (target.getAttribute("data-variant-id")) {
        return variant.id == target.getAttribute("data-variant-id");
      }
      // Otherwise, match the correct optionN property
      return optionProp && variant[optionProp] === target?.value;
    });
  }

  const selectedVariantKey = stockLevels?.findIndex((lvl: any) => lvl.title === selectedVariant?.title);
  const isOos = target?.closest(".button")?.classList.contains("oos");
  const selectedStockLevel = stockLevels?.find((lvl: any) => lvl.title === selectedVariant?.title)?.level ?? null;

  if (target && !isGiftCard) {
    if (sizeText !== null && sizeText !== undefined) {
      //@ts-ignore
      sizeText.innerHTML = `<strong>Size:</strong> <span>${target.value}</span>`;
    }
  }
  // if (target?.getAttribute("data-variant-id")) {
  //   selectedVariant = target?.getAttribute("data-variant-id");
  // }

  if (!selectedVariant) return;

  if (isProduct) {
    setPricing(selectedVariant.price, selectedVariant.compare_at_price);
    isProduct = false;
  }

  const selectedId = selectedVariant.id.toString();

  hiddenId.value = selectedId;
  if (!isGiftCard) {
    hiddenBisId.value = selectedId;
  }

  disableAtcOnNotSelected();

  const lowStockMsg = document.querySelector(".product__low-stock");
  const oosStockMsg = document.querySelector(".product__out-of-stock");

  // Social Proof Stock Count
  if (stockLevels && selectedVariantKey) {
    const stock_count = Number(stockLevels[selectedVariantKey]?.quantity);

    if (stock_count && stock_count > 0 && stock_count < 10 && lowStockMsg && window?.location?.href.includes("australia")) {
      if (stock_count >= 3) {
        lowStockMsg.innerHTML = `<strong>Only ${stock_count - 2} left in stock</strong>`;
      } else {
        lowStockMsg.innerHTML = `<strong>Only ${stock_count} left in stock</strong>`;
      }
    }
  }

  errorMessage?.classList.add("hidden");
  lowStockMsg?.classList.add("hidden");
  oosStockMsg?.classList.add("hidden");

  if (selectedStockLevel === "low_stock") {
    lowStockMsg?.classList.remove("hidden");
  }
  if (selectedStockLevel === "out_of_stock") {
    oosStockMsg?.classList.remove("hidden");
  }

  if (isOos || isComingSoon) {
    oosDetails?.classList.remove("hidden");
    productQty?.classList.add("hidden");
    addToCartWrapper?.classList.add("hidden");
    if (window.location.href.includes("email=")) {
      (document.querySelector(".product_oos-message-content .sold_out") as HTMLElement).classList.add("hidden");
      (document.querySelector(".product_oos-message-content .success") as HTMLElement).classList.remove("hidden");
      (document.querySelector(".product_oos-message-content .sold_out-p") as HTMLElement).classList.add("hidden");
      (document.querySelector(".product_oos-message-content .success-p") as HTMLElement).classList.remove("hidden");
    } else {
      (document.querySelector(".product_oos-message-content .sold_out") as HTMLElement).classList.remove("hidden");
      (document.querySelector(".product_oos-message-content .success") as HTMLElement).classList.add("hidden");
      (document.querySelector(".product_oos-message-content .sold_out-p") as HTMLElement).classList.remove("hidden");
      (document.querySelector(".product_oos-message-content .success-p") as HTMLElement).classList.add("hidden");
    }
    (document.querySelector(".product__bis-form-email input") as HTMLInputElement).focus();
  } else {
    oosDetails?.classList.add("hidden");
    productQty?.classList.remove("hidden");
    addToCartWrapper?.classList.remove("hidden");
  }

  handleVariantParamChange(selectedId);

  window.dispatchEvent(new CustomEvent("variantchange", { detail: selectedVariant.id }));
}

export function handleVariantChange({
  e,
  productBISForm,
  product,
  stockLevels,
  isGiftCard,
  sizeText,
  isProduct,
  setPricing,
  errorMessage,
  isComingSoon,
  addToCartWrapper,
}: VariantChangeArgs) {
  const target = e?.target as HTMLInputElement;
  if (target !== null) {
    variantChange({
      target,
      productBISForm,
      product,
      stockLevels,
      isGiftCard,
      sizeText,
      isProduct,
      setPricing,
      errorMessage,
      isComingSoon,
      addToCartWrapper,
    });
  }
  // this.isGiftCard ? this.giftVariantChange(e) : this.variantChange(e.target);
}
export function disableAtcOnNotSelected() {
  const atcButton = document.querySelector(".add-to-cart") as HTMLButtonElement;
  const id = document.querySelector("input[name=id]") as HTMLInputElement;

  atcButton.disabled = !id.value.length ? true : false;
  atcButton.ariaDisabled = !id.value.length ? "true" : "false";
}
function handleVariantParamChange(selectedId: string) {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const variant = params.get("variant");
  if (variant === selectedId) return;
  params.set("variant", selectedId);
  const newurl =
    window.location.protocol + "//" + window.location.host + window.location.pathname + "?" + params.toString();
  window.history.replaceState({ path: newurl }, "", newurl);
}
export function disableAddToCartButton() {
  const atcButton = document.querySelector(".add-to-cart") as HTMLButtonElement;
  const id = document.querySelector("input[name=id]") as HTMLInputElement;
  atcButton.disabled = true;
  atcButton.ariaDisabled = "true";
}
